import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const texts = {
  de: {
    badge: 'Termin buchen',
    title: 'DEIN KOSTENFREIER POTENZIAL-CHECK.',
    sub: 'Kein Verkaufsgespräch, kein Pitch. In 20 Minuten klären wir gemeinsam: Wo hat KI-Implementierung in deinem Betrieb den größten Hebel – und macht eine Zusammenarbeit Sinn?',
    loadingSlots: 'Verfügbare Termine werden geladen...',
    noSlots: 'Aktuell keine freien Termine. Bitte später nochmal probieren oder per Chat anfragen.',
    pickDay: 'Wann passt es dir?',
    pickTime: 'Welche Uhrzeit?',
    yourData: 'Deine Daten',
    name: 'Name',
    email: 'E-Mail',
    company: 'Unternehmen (optional)',
    note: 'Worum geht es? (optional)',
    confirm: 'Termin verbindlich buchen',
    booking: 'Wird gebucht...',
    back: 'Zurück',
    success: 'Termin bestätigt!',
    successText: 'Du erhältst gleich eine Bestätigungs-E-Mail mit dem Google Meet-Link.',
    error: 'Fehler beim Buchen',
  },
  en: {
    badge: 'Book a call',
    title: 'Book your 20-minute potential check',
    sub: 'In max. 20 minutes we take a first, honest look at your business. Pick a suitable time — your slot is confirmed instantly and you\'ll receive your Google Meet link right after.',
    loadingSlots: 'Loading available times...',
    noSlots: 'No free slots right now. Please try again later or use the chat.',
    pickDay: 'When works for you?',
    pickTime: 'Which time?',
    yourData: 'Your details',
    name: 'Name',
    email: 'Email',
    company: 'Company (optional)',
    note: 'What is it about? (optional)',
    confirm: 'Confirm booking',
    booking: 'Booking...',
    back: 'Back',
    success: 'Booking confirmed!',
    successText: 'You\'ll receive a confirmation email with the Google Meet link shortly.',
    error: 'Booking failed',
  },
};

export default function BookingSection() {
  const { lang } = useLanguage();
  const t = texts[lang];

  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', company: '', note: '' });
  const [booking, setBooking] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/public/available-slots`)
      .then(({ data }) => { setDays(data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const step = success ? 'done' : selectedTime ? 'form' : selectedDay ? 'time' : 'day';

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !selectedTime) return;
    setBooking(true); setError('');
    try {
      const { data } = await axios.post(`${API}/public/book-slot`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        note: form.note.trim(),
        datetime_iso: selectedTime.datetime,
      });
      setSuccess(data);
    } catch (err) {
      setError(err.response?.data?.detail || t.error);
    }
    setBooking(false);
  };

  const resetToDay = () => { setSelectedTime(null); };
  const resetToStart = () => { setSelectedTime(null); setSelectedDay(null); };

  return (
    <section id="booking" data-testid="booking-section" className="bg-[#f5f5f5] py-24 md:py-32">
      <div className="max-w-[900px] mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <p className="text-[#befa0a] text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {t.badge}
          </p>
          <h2 className="text-[#1f1f1f] text-3xl md:text-4xl font-bold uppercase tracking-tight leading-[1.3] mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {t.title}
          </h2>
          <p className="text-[#1f1f1f]/60 text-base max-w-xl mx-auto" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {t.sub}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-2xl shadow-sm border border-[#e5e5e5] p-6 md:p-10"
        >
          {loading && (
            <p className="text-center text-gray-400 py-12" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {t.loadingSlots}
            </p>
          )}

          {!loading && days.length === 0 && step !== 'done' && (
            <p className="text-center text-gray-400 py-12" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {t.noSlots}
            </p>
          )}

          {/* Step 1: Pick day */}
          {!loading && days.length > 0 && step === 'day' && (
            <>
              <p className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {t.pickDay}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {days.map(d => {
                  const free = d.free_count ?? d.times.filter(x => x.available).length;
                  const disabled = free === 0;
                  return (
                    <button key={d.date} onClick={() => !disabled && setSelectedDay(d)} disabled={disabled}
                      className={`border-2 rounded-lg p-4 text-center transition-all ${
                        disabled
                          ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                          : 'border-gray-200 bg-white hover:border-[#befa0a] hover:bg-[#befa0a]/5'
                      }`}>
                      <p className={`font-bold text-base ${disabled ? 'text-gray-400' : 'text-[#1f1f1f]'}`} style={{ fontFamily: "'Oswald', sans-serif" }}>{d.short_label}</p>
                      <p className={`text-xs mt-1.5 font-medium ${disabled ? 'text-gray-400' : 'text-[#658a07]'}`} style={{ fontFamily: "'Manrope', sans-serif" }}>
                        {disabled ? 'ausgebucht' : `${free} frei`}
                      </p>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Step 2: Pick time */}
          {step === 'time' && selectedDay && (
            <>
              <button onClick={resetToStart} className="flex items-center gap-1 text-gray-400 hover:text-gray-700 text-sm mb-4">
                <ArrowLeft size={14} /> {t.back}
              </button>
              <p className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {t.pickTime}
              </p>
              <p className="text-[#1f1f1f] font-bold mb-5" style={{ fontFamily: "'Oswald', sans-serif" }}>{selectedDay.full_label}</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {selectedDay.times.map(slot => (
                  <button key={slot.datetime} onClick={() => slot.available && setSelectedTime(slot)} disabled={!slot.available}
                    className={`border-2 transition-all rounded-lg py-2.5 text-sm font-bold ${
                      slot.available
                        ? 'border-[#befa0a] text-[#658a07] bg-white hover:bg-[#befa0a] hover:text-[#0a0a0a] cursor-pointer'
                        : 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed line-through'
                    }`}
                    style={{ fontFamily: "'Manrope', sans-serif" }}>
                    {slot.time}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Step 3: Form */}
          {step === 'form' && selectedDay && selectedTime && (
            <>
              <button onClick={resetToDay} className="flex items-center gap-1 text-gray-400 hover:text-gray-700 text-sm mb-4">
                <ArrowLeft size={14} /> {t.back}
              </button>
              <div className="bg-[#befa0a]/10 border border-[#befa0a]/40 rounded-lg p-4 mb-6">
                <p className="text-sm text-[#1f1f1f]" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  <strong>{selectedDay.full_label}</strong> · {selectedTime.time} Uhr
                </p>
              </div>
              <p className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {t.yourData}
              </p>
              <form onSubmit={submit} className="space-y-3">
                <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder={t.name} required
                  className="w-full border border-gray-300 rounded px-4 py-3 text-sm focus:outline-none focus:border-[#befa0a]"
                  style={{ fontFamily: "'Manrope', sans-serif" }} />
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder={t.email} required
                  className="w-full border border-gray-300 rounded px-4 py-3 text-sm focus:outline-none focus:border-[#befa0a]"
                  style={{ fontFamily: "'Manrope', sans-serif" }} />
                <input type="text" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} placeholder={t.company}
                  className="w-full border border-gray-300 rounded px-4 py-3 text-sm focus:outline-none focus:border-[#befa0a]"
                  style={{ fontFamily: "'Manrope', sans-serif" }} />
                <textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder={t.note} rows={3}
                  className="w-full border border-gray-300 rounded px-4 py-3 text-sm focus:outline-none focus:border-[#befa0a] resize-none"
                  style={{ fontFamily: "'Manrope', sans-serif" }} />
                {error && <p className="text-red-500 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>{error}</p>}
                <button type="submit" disabled={booking}
                  className="w-full bg-[#befa0a] text-[#0a0a0a] py-4 font-bold uppercase tracking-wider rounded hover:bg-lime-300 transition-colors disabled:opacity-50"
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {booking ? t.booking : t.confirm}
                </button>
              </form>
            </>
          )}

          {/* Step 4: Success */}
          {step === 'done' && (
            <div className="text-center py-8">
              <div className="w-1.5 h-12 bg-[#befa0a] mx-auto mb-5" />
              <h3 className="text-[#1f1f1f] text-2xl font-bold uppercase mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {t.success}
              </h3>
              <p className="text-gray-500 text-base mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {t.successText}
              </p>
              {success?.meet_link && (
                <a href={success.meet_link} target="_blank" rel="noopener noreferrer"
                  className="inline-block mt-3 text-[#658a07] hover:underline text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  → Google Meet Link öffnen
                </a>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
